import { Workflow } from "../../Workflows/Models/Workflow";
import { LoadingState } from "../../Common";
import {
  TopicWorkflowAction,
  TopicWorkflowActionType,
  ITopicWorkflowsListLoadedPayload,
  ISetTopicWorkflowsErroredPayload,
  IWorkflowListUpdateListItemPayload,
  IWorkflowListRemoveWorkflowFromListPayload
} from "../Actions/TopicWorkflowActions";

export interface TopicWorkflowState {
  workflows: Workflow[];
  loadingState: LoadingState;
  errorMessage: string;
  errorCode?: number;
}

const initialTopicWorkflowState: TopicWorkflowState = {
  workflows: [],
  loadingState: {
    isLoaded: false,
    isLoading: false,
    errored: false
  },
  errorMessage: ""
};

const updateObject = (oldObject: any, newValues: any) => ({ ...oldObject, ...newValues });

function updateWorkflowInList(
  existingWorkflowList: Workflow[],
  updatedWorkflow: Workflow
): Workflow[] {
  return existingWorkflowList.map((workflow, index) => {
    // check to see if this is the item we need to update
    if (workflow.code === updatedWorkflow.code) {
      // this is the one to update, return an updated value
      return {
        ...workflow,
        ...updatedWorkflow,
        updateCount: (workflow.updateCount == null ? 0 : workflow.updateCount) + 1
      };
    }
    // this is not the item we want to update, leave it as is
    return workflow;
  });
}

const topicWorkflowReducer = (
  topicWorkflowState: TopicWorkflowState = initialTopicWorkflowState,
  action: TopicWorkflowAction
) => {
  switch (action.type) {
    case TopicWorkflowActionType.TOPIC_WORKFLOW_LIST_LOADED: {
      const payload = <ITopicWorkflowsListLoadedPayload>action.payload;

      const { loadedWorkflows } = payload;

      // init the update count for each workflow (0 updates so far as this is the first time its loaded)
      for (let i = 0; i < loadedWorkflows.length; i++) {
        loadedWorkflows[i].updateCount = 0;
      }

      return updateObject(topicWorkflowState, {
        workflows: loadedWorkflows,
        loadingState: {
          isLoaded: true,
          isLoading: false,
          errored: false
        }
      });
    }
    case TopicWorkflowActionType.TOPIC_WORKFLOW_LIST_UNLOAD: {
      return updateObject(topicWorkflowState, {
        workflows: [],
        loadingState: {
          isLoaded: false,
          isLoading: false,
          errored: false
        }
      });
    }
    case TopicWorkflowActionType.TOPIC_WORKFLOW_LIST_SET_LOADING: {
      return updateObject(topicWorkflowState, {
        workflows: [],
        loadingState: {
          isLoaded: false,
          isLoading: true,
          errored: false
        }
      });
    }
    case TopicWorkflowActionType.TOPIC_WORKFLOW_LIST_SET_ERRORED: {
      const payload = <ISetTopicWorkflowsErroredPayload>action.payload;
      return updateObject(topicWorkflowState, {
        workflows: [],
        loadingState: {
          isLoaded: false,
          isLoading: false,
          errored: true
        },
        errorMessage: payload.errorMessage,
        errorCode: payload.errorCode
      });
    }
    case TopicWorkflowActionType.TOPIC_WORKFLOW_LIST_UPDATE_LIST_ITEM: {
      const payload = <IWorkflowListUpdateListItemPayload>action.payload;

      // update the workflows array immutably
      const newWorkflows = updateWorkflowInList(
        topicWorkflowState.workflows,
        payload.updatedWorkflow
      );

      // now update the state
      return updateObject(topicWorkflowState, {
        workflows: newWorkflows,
        loadingState: {
          isLoaded: true,
          isLoading: false,
          errored: false
        }
      });
    }
    case TopicWorkflowActionType.TOPIC_WORKFLOW_LIST_REMOVE_ITEM: {
      const payload = <IWorkflowListRemoveWorkflowFromListPayload>action.payload;

      // remove the workflow from the array immutably
      const newWorkflows = topicWorkflowState.workflows.filter(
        item => item.code !== payload.workflowCode
      );

      // now update the state
      return updateObject(topicWorkflowState, {
        workflows: newWorkflows,
        loadingState: {
          isLoaded: true,
          isLoading: false,
          errored: false
        }
      });
    }
    default:
      return topicWorkflowState;
  }
};

export default topicWorkflowReducer;
